import React, { useState, useEffect } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  TextInput,
  Select,
  ActionIcon,
  Grid,
  Tabs,
  Alert,
  Modal,
  Divider,
  Progress,
  ThemeIcon,
  Textarea,
  Radio,
  Checkbox,
  Timeline,
  Table,
  NumberInput,
  SegmentedControl,
  MultiSelect,
  Indicator,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import {
  IconSearch,
  IconFilter,
  IconClock,
  IconEdit,
  IconSend,
  IconFileText,
  IconRefresh,
  IconChevronLeft,
  IconMoodHappy,
  IconMoodSad,
  IconMoodNeutral,
  IconSun,
  IconApple,
  IconCamera,
  IconMessage,
  IconStar,
  IconPlayerPlay,
  IconTrash,
  IconPlus,
  IconCalendar,
  IconDownload,
  IconPrinter,
  IconEye,
  IconCopy,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { 
  useTeacherClasses,
  useClassStudents,
  useDailyReports,
  useUpdateDailyReport,
  type DailyReport,
  type TeacherStudent,
} from '../../hooks/useTeacher';
import { SmartLoader } from '../../components/common/LoadingStates';

const DailyReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedStudent, setSelectedStudent] = useState<TeacherStudent | null>(null);
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);
  const [reportModalOpened, { open: openReportModal, close: closeReportModal }] = useDisclosure(false);
  const [previewModalOpened, { open: openPreviewModal, close: closePreviewModal }] = useDisclosure(false);

  const updateReportMutation = useUpdateDailyReport();

  const {
    data: classes,
    isLoading: classesLoading,
  } = useTeacherClasses();

  const {
    data: students,
    isLoading: studentsLoading,
  } = useClassStudents(selectedClassId);

  const {
    data: reports,
    isLoading: reportsLoading,
    error: reportsError,
    refetch: refetchReports,
  } = useDailyReports(selectedDate.toISOString().split('T')[0]);

  // Set initial class when classes are loaded
  useEffect(() => {
    if (classes && classes.length > 0 && !selectedClassId) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId]);

  // Form state for report editing
  const [reportForm, setReportForm] = useState<Partial<DailyReport>>({
    mood: 'happy',
    meals: {
      breakfast: { eaten: false, amount: 'none' },
      lunch: { eaten: false, amount: 'none' },
      snack: { eaten: false, amount: 'none' },
    },
    nap: {
      quality: 'no_nap',
    },
    activities: [],
    behavior: {
      overall: 'good',
      highlights: [],
      concerns: [],
    },
    milestones: [],
    teacher_notes: '',
    parent_communication: {
      message: '',
      requires_response: false,
    },
    photos: [],
  });

  const filteredStudents = students?.filter(student => {
    const matchesSearch = 
      student.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const report = reports?.find(r => r.student_id === student.id);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'completed' && report?.status === 'completed') ||
      (statusFilter === 'draft' && report?.status === 'draft') ||
      (statusFilter === 'sent' && report?.status === 'sent') ||
      (statusFilter === 'pending' && !report);
    
    return matchesSearch && matchesStatus;
  }) || [];

  const stats = {
    total: students?.length || 0,
    completed: reports?.filter(r => r.status === 'completed' || r.status === 'sent').length || 0,
    draft: reports?.filter(r => r.status === 'draft').length || 0,
    pending: (students?.length || 0) - (reports?.length || 0),
  };

  const getMoodIcon = (mood: string) => {
    switch (mood) {
      case 'happy':
      case 'excited': return <IconMoodHappy size={20} />;
      case 'sad':
      case 'upset': return <IconMoodSad size={20} />;
      default: return <IconMoodNeutral size={20} />;
    }
  };

  const getMoodColor = (mood: string) => {
    switch (mood) {
      case 'happy':
      case 'excited': return 'green';
      case 'sad':
      case 'upset': return 'red';
      case 'tired': return 'orange';
      default: return 'blue';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'green';
      case 'completed': return 'blue';
      case 'draft': return 'orange';
      default: return 'gray';
    }
  };

  const handleCreateReport = (student: TeacherStudent) => {
    setSelectedStudent(student);
    const existingReport = reports?.find(r => r.student_id === student.id);
    
    if (existingReport) {
      setSelectedReport(existingReport as any);
      setReportForm({
        ...existingReport,
        arrival_time: existingReport.arrival_time || `${new Date().getHours()}:${new Date().getMinutes()}`,
      } as any);
    } else {
      const newReport: Partial<DailyReport> = {
        student_id: student.id,
        teacher_id: '1',
        date: selectedDate.toISOString().split('T')[0],
        arrival_time: '8:30 AM',
        mood: 'happy',
        meals: {
          breakfast: { eaten: false, amount: 'none' },
          lunch: { eaten: false, amount: 'none' },
          snack: { eaten: false, amount: 'none' },
        },
        nap: {
          quality: 'no_nap',
        },
        activities: [],
        behavior: {
          overall: 'good',
          highlights: [],
          concerns: [],
        },
        milestones: [],
        teacher_notes: '',
        parent_communication: {
          message: '',
          requires_response: false,
        },
        photos: [],
        status: 'draft',
      };
      setSelectedReport(null);
      setReportForm(newReport);
    }
    
    openReportModal();
  };

  const handleSaveReport = async (status: 'draft' | 'completed' | 'sent' = 'draft') => {
    if (!selectedStudent) return;

    try {
      await updateReportMutation.mutateAsync({
        id: selectedReport?.id || '',
        report: {
          ...reportForm,
          status,
        } as any
      });

      notifications.show({
        title: 'Report Saved',
        message: `Daily report for ${selectedStudent.first_name} has been ${status === 'sent' ? 'sent to parents' : 'saved'}`,
        color: 'green',
      });

      closeReportModal();
      refetchReports();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save report',
        color: 'red',
      });
    }
  };

  const handlePreviewReport = (student: TeacherStudent) => {
    const report = reports?.find(r => r.student_id === student.id);
    if (report) {
      setSelectedStudent(student);
      setSelectedReport(report as any);
      openPreviewModal();
    }
  };

  const handleAddActivity = () => {
    setReportForm(prev => ({
      ...prev,
      activities: [
        ...(prev.activities || []),
        {
          type: '',
          duration: 30,
          participation: 'good' as const,
          notes: '',
        },
      ],
    }));
  };

  const handleRemoveActivity = (index: number) => {
    setReportForm(prev => ({
      ...prev,
      activities: prev.activities?.filter((_, i) => i !== index) || [],
    }));
  };

  const activityTypes = [
    'Art & Crafts',
    'Story Time',
    'Music & Movement',
    'Outdoor Play',
    'Learning Centers',
    'Circle Time',
    'Free Play',
    'Science Activity',
    'Math Activity',
    'Sensory Play',
  ];

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Daily Reports</Title>
              <Text size="sm" c="dimmed">
                Create and manage daily reports for students
              </Text>
            </div>
          </Group>
          <Group>
            <DatePickerInput
              value={selectedDate}
              onChange={(date) => setSelectedDate(date || new Date())}
              maxDate={new Date()}
              leftSection={<IconCalendar size={16} />}
              placeholder="Select date"
            />
            <ActionIcon variant="subtle" onClick={() => refetchReports()}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </Card>

      {/* Statistics */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Students</Text>
          <Text fw={700} size="xl">{stats.total}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Completed</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="green">{stats.completed}</Text>
            <Progress value={(stats.completed / stats.total) * 100} color="green" size="sm" style={{ width: 60 }} />
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">In Progress</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="orange">{stats.draft}</Text>
            <Badge color="orange" size="xs">Draft</Badge>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Pending</Text>
          <Text fw={700} size="xl" c="red">{stats.pending}</Text>
        </Card>
      </SimpleGrid>

      {/* Class Selector */}
      <SmartLoader isLoading={classesLoading}>
        {classes && classes.length > 1 && (
          <Card withBorder>
            <Title order={4} mb="md">Select Class</Title>
            <SegmentedControl
              value={selectedClassId.toString()}
              onChange={(value) => setSelectedClassId(value)}
              data={classes.filter(cls => cls && cls.name).map(cls => ({
                label: `${cls.name} (${cls.student_count || 0})`,
                value: cls.id.toString(),
              }))}
              fullWidth
            />
          </Card>
        )}
      </SmartLoader>

      {/* Filters */}
      <Card withBorder>
        <Title order={4} mb="md">Filters</Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              placeholder="Search students..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Select
              placeholder="Report status"
              leftSection={<IconFilter size={16} />}
              data={[
                { value: 'all', label: 'All Students' },
                { value: 'completed', label: 'Completed' },
                { value: 'draft', label: 'In Progress' },
                { value: 'sent', label: 'Sent' },
                { value: 'pending', label: 'Not Started' },
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'all')}
            />
          </Grid.Col>
        </Grid>
      </Card>

      {/* Students List */}
      <SmartLoader
        isLoading={studentsLoading || reportsLoading}
        error={reportsError as Error | null}
        isEmpty={!filteredStudents.length}
        emptyState={
          <Alert color="blue" title="No students found">
            {searchQuery || statusFilter !== 'all' 
              ? "No students match your current filters."
              : "No students in this class."
            }
          </Alert>
        }
      >
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {filteredStudents.map((student) => {
            const report = reports?.find(r => r.student_id === student.id);
            
            return (
              <Card key={student.id} withBorder radius="md">
                <Group justify="space-between" mb="sm">
                  <Group>
                    <Avatar size="lg" radius="xl">
                      {student.first_name[0]}{student.last_name[0]}
                    </Avatar>
                    <div>
                      <Text fw={500}>{student.first_name} {student.last_name}</Text>
                      <Text size="sm" c="dimmed">Age {student.age}</Text>
                    </div>
                  </Group>
                  {report && (
                    <Indicator color={getStatusColor(report.status || 'draft')} processing={report.status === 'draft'}>
                      <Badge color={getStatusColor(report.status || 'draft')}>
                        {report.status}
                      </Badge>
                    </Indicator>
                  )}
                </Group>

                {report ? (
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Mood</Text>
                      <Group gap={4}>
                        <ThemeIcon 
                          color={getMoodColor(report.mood)} 
                          variant="light" 
                          size="sm"
                        >
                          {getMoodIcon(report.mood)}
                        </ThemeIcon>
                        <Text size="sm" tt="capitalize">{report.mood}</Text>
                      </Group>
                    </Group>

                    {report.arrival_time && (
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Arrival</Text>
                        <Text size="sm">{report.arrival_time}</Text>
                      </Group>
                    )}

                    {report.activities.length > 0 && (
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Activities</Text>
                        <Badge size="sm" variant="light">
                          {report.activities.length} recorded
                        </Badge>
                      </Group>
                    )}

                    <Divider />

                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconEdit size={14} />}
                        onClick={() => handleCreateReport(student as any)}
                        fullWidth
                      >
                        {report.status === 'draft' ? 'Continue' : 'Edit'}
                      </Button>
                      {report.status !== 'draft' && (
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          leftSection={<IconEye size={14} />}
                          onClick={() => handlePreviewReport(student as any)}
                          fullWidth
                        >
                          Preview
                        </Button>
                      )}
                    </Group>
                  </Stack>
                ) : (
                  <Stack gap="sm">
                    <Alert color="gray" p="xs">
                      <Text size="sm">No report created yet</Text>
                    </Alert>
                    <Button
                      size="sm"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => handleCreateReport(student as any)}
                      fullWidth
                    >
                      Create Report
                    </Button>
                  </Stack>
                )}
              </Card>
            );
          })}
        </SimpleGrid>
      </SmartLoader>

      {/* Report Creation/Edit Modal */}
      <Modal
        opened={reportModalOpened}
        onClose={closeReportModal}
        title={selectedStudent ? `Daily Report - ${selectedStudent.first_name} ${selectedStudent.last_name}` : 'Daily Report'}
        size="xl"
        fullScreen
      >
        {selectedStudent && (
          <Stack gap="md">
            <Group justify="space-between">
              <Group>
                <Avatar size="lg" radius="xl">
                  {selectedStudent.first_name[0]}{selectedStudent.last_name[0]}
                </Avatar>
                <div>
                  <Text fw={500} size="lg">
                    {selectedStudent.first_name} {selectedStudent.last_name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {selectedDate.toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </Text>
                </div>
              </Group>
              <Group>
                <Button
                  variant="light"
                  leftSection={<IconEye size={16} />}
                  onClick={() => {
                    closeReportModal();
                    openPreviewModal();
                  }}
                >
                  Preview
                </Button>
                <Button
                  variant="light"
                  color="orange"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => handleSaveReport('draft')}
                >
                  Save Draft
                </Button>
                <Button
                  color="green"
                  leftSection={<IconSend size={16} />}
                  onClick={() => handleSaveReport('sent')}
                >
                  Send to Parents
                </Button>
              </Group>
            </Group>

            <Divider />

            <Tabs defaultValue="general">
              <Tabs.List>
                <Tabs.Tab value="general" leftSection={<IconSun size={16} />}>
                  General
                </Tabs.Tab>
                <Tabs.Tab value="meals" leftSection={<IconApple size={16} />}>
                  Meals
                </Tabs.Tab>
                <Tabs.Tab value="activities" leftSection={<IconPlayerPlay size={16} />}>
                  Activities
                </Tabs.Tab>
                <Tabs.Tab value="behavior" leftSection={<IconStar size={16} />}>
                  Behavior
                </Tabs.Tab>
                <Tabs.Tab value="communication" leftSection={<IconMessage size={16} />}>
                  Parent Communication
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="general" pt="md">
                <Grid>
                  <Grid.Col span={6}>
                    <TimeInput
                      label="Arrival Time"
                      leftSection={<IconClock size={16} />}
                      value={reportForm.arrival_time || ''}
                      onChange={(event) => setReportForm(prev => ({
                        ...prev,
                        arrival_time: event.currentTarget.value,
                      }))}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TimeInput
                      label="Departure Time"
                      leftSection={<IconClock size={16} />}
                      value={reportForm.departure_time || ''}
                      onChange={(event) => setReportForm(prev => ({
                        ...prev,
                        departure_time: event.currentTarget.value,
                      }))}
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Radio.Group
                      label="Overall Mood"
                      value={reportForm.mood}
                      onChange={(value) => setReportForm(prev => ({ ...prev, mood: value as any }))}
                    >
                      <Group mt="xs">
                        <Radio value="happy" label="Happy" />
                        <Radio value="excited" label="Excited" />
                        <Radio value="neutral" label="Neutral" />
                        <Radio value="tired" label="Tired" />
                        <Radio value="sad" label="Sad" />
                        <Radio value="upset" label="Upset" />
                      </Group>
                    </Radio.Group>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Card withBorder>
                      <Title order={5} mb="sm">Nap Time</Title>
                      <Grid>
                        <Grid.Col span={4}>
                          <TimeInput
                            label="Start Time"
                            value={reportForm.nap?.start_time || ''}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              nap: {
                                ...prev.nap!,
                                start_time: event.currentTarget.value,
                              },
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <TimeInput
                            label="End Time"
                            value={reportForm.nap?.end_time || ''}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              nap: {
                                ...prev.nap!,
                                end_time: event.currentTarget.value,
                              },
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <Select
                            label="Quality"
                            data={[
                              { value: 'excellent', label: 'Excellent' },
                              { value: 'good', label: 'Good' },
                              { value: 'fair', label: 'Fair' },
                              { value: 'poor', label: 'Poor' },
                              { value: 'no_nap', label: 'No Nap' },
                            ]}
                            value={reportForm.nap?.quality}
                            onChange={(value) => setReportForm(prev => ({
                              ...prev,
                              nap: {
                                ...prev.nap!,
                                quality: value as any,
                              },
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea
                            label="Nap Notes"
                            placeholder="Any notes about nap time..."
                            value={reportForm.nap?.notes || ''}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              nap: {
                                ...prev.nap!,
                                notes: event.currentTarget.value,
                              },
                            }))}
                            rows={2}
                          />
                        </Grid.Col>
                      </Grid>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Tabs.Panel>

              <Tabs.Panel value="meals" pt="md">
                <Stack gap="md">
                  {['breakfast', 'lunch', 'snack'].map((mealType) => (
                    <Card key={mealType} withBorder>
                      <Title order={5} mb="sm" tt="capitalize">{mealType}</Title>
                      <Grid>
                        <Grid.Col span={6}>
                          <Checkbox
                            label="Ate this meal"
                            checked={reportForm.meals?.[mealType as keyof typeof reportForm.meals]?.eaten || false}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              meals: {
                                ...prev.meals!,
                                [mealType]: {
                                  ...((prev.meals![mealType as keyof typeof prev.meals] as any) || {}),
                                  eaten: event.currentTarget.checked,
                                },
                              },
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Select
                            label="Amount"
                            data={[
                              { value: 'none', label: 'None' },
                              { value: 'little', label: 'A Little' },
                              { value: 'some', label: 'Some' },
                              { value: 'all', label: 'All' },
                            ]}
                            value={reportForm.meals?.[mealType as keyof typeof reportForm.meals]?.amount || 'none'}
                            onChange={(value) => setReportForm(prev => ({
                              ...prev,
                              meals: {
                                ...prev.meals!,
                                [mealType]: {
                                  ...((prev.meals![mealType as keyof typeof prev.meals] as any) || {}),
                                  amount: value as any,
                                },
                              },
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <TextInput
                            label="Notes"
                            placeholder={`Notes about ${mealType}...`}
                            value={reportForm.meals?.[mealType as keyof typeof reportForm.meals]?.notes || ''}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              meals: {
                                ...prev.meals!,
                                [mealType]: {
                                  ...((prev.meals![mealType as keyof typeof prev.meals] as any) || {}),
                                  notes: event.currentTarget.value,
                                },
                              },
                            }))}
                          />
                        </Grid.Col>
                      </Grid>
                    </Card>
                  ))}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="activities" pt="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={5}>Activities</Title>
                    <Button
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={handleAddActivity}
                    >
                      Add Activity
                    </Button>
                  </Group>

                  {reportForm.activities?.map((activity, index) => (
                    <Card key={index} withBorder>
                      <Group justify="space-between" mb="sm">
                        <Title order={6}>Activity {index + 1}</Title>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => handleRemoveActivity(index)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                      <Grid>
                        <Grid.Col span={6}>
                          <Select
                            label="Activity Type"
                            data={activityTypes}
                            value={activity.type}
                            onChange={(value) => setReportForm(prev => ({
                              ...prev,
                              activities: prev.activities?.map((a, i) => 
                                i === index ? { ...a, type: value || '' } : a
                              ) || [],
                            }))}
                            required
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <NumberInput
                            label="Duration (min)"
                            value={activity.duration}
                            onChange={(value) => setReportForm(prev => ({
                              ...prev,
                              activities: prev.activities?.map((a, i) => 
                                i === index ? { ...a, duration: Number(value) || 0 } : a
                              ) || [],
                            }))}
                            min={5}
                            max={120}
                            step={5}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <Select
                            label="Participation"
                            data={[
                              { value: 'excellent', label: 'Excellent' },
                              { value: 'good', label: 'Good' },
                              { value: 'fair', label: 'Fair' },
                              { value: 'poor', label: 'Poor' },
                            ]}
                            value={activity.participation}
                            onChange={(value) => setReportForm(prev => ({
                              ...prev,
                              activities: prev.activities?.map((a, i) => 
                                i === index ? { ...a, participation: value as any } : a
                              ) || [],
                            }))}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea
                            label="Notes"
                            placeholder="Activity notes..."
                            value={activity.notes || ''}
                            onChange={(event) => setReportForm(prev => ({
                              ...prev,
                              activities: prev.activities?.map((a, i) => 
                                i === index ? { ...a, notes: event.currentTarget.value } : a
                              ) || [],
                            }))}
                            rows={2}
                          />
                        </Grid.Col>
                      </Grid>
                    </Card>
                  ))}

                  {reportForm.activities?.length === 0 && (
                    <Alert color="blue">
                      No activities recorded yet. Click "Add Activity" to start.
                    </Alert>
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="behavior" pt="md">
                <Stack gap="md">
                  <Radio.Group
                    label="Overall Behavior"
                    value={reportForm.behavior?.overall}
                    onChange={(value) => setReportForm(prev => ({
                      ...prev,
                      behavior: {
                        ...prev.behavior!,
                        overall: value as any,
                      },
                    }))}
                  >
                    <Group mt="xs">
                      <Radio value="excellent" label="Excellent" />
                      <Radio value="good" label="Good" />
                      <Radio value="fair" label="Fair" />
                      <Radio value="needs_improvement" label="Needs Improvement" />
                    </Group>
                  </Radio.Group>

                  <MultiSelect
                    label="Behavior Highlights"
                    placeholder="Select positive behaviors"
                    data={[
                      'Shared toys nicely',
                      'Helped clean up',
                      'Was kind to friends',
                      'Followed directions well',
                      'Participated actively',
                      'Showed leadership',
                      'Was helpful',
                      'Good listener',
                    ]}
                    value={reportForm.behavior?.highlights || []}
                    onChange={(value) => setReportForm(prev => ({
                      ...prev,
                      behavior: {
                        ...prev.behavior!,
                        highlights: value,
                      },
                    }))}
                  />

                  <MultiSelect
                    label="Areas of Concern"
                    placeholder="Select any concerns (optional)"
                    data={[
                      'Had difficulty sharing',
                      'Needed reminders to listen',
                      'Was upset at times',
                      'Had conflicts with peers',
                      'Struggled with transitions',
                      'Needed extra support',
                    ]}
                    value={reportForm.behavior?.concerns || []}
                    onChange={(value) => setReportForm(prev => ({
                      ...prev,
                      behavior: {
                        ...prev.behavior!,
                        concerns: value,
                      },
                    }))}
                  />

                  <Textarea
                    label="Behavior Notes"
                    placeholder="Additional notes about behavior..."
                    value={reportForm.behavior?.notes || ''}
                    onChange={(event) => setReportForm(prev => ({
                      ...prev,
                      behavior: {
                        ...prev.behavior!,
                        notes: event.currentTarget.value,
                      },
                    }))}
                    rows={3}
                  />

                  <MultiSelect
                    label="Milestones Achieved"
                    placeholder="Select any milestones"
                    data={[
                      'Counted to 10',
                      'Counted to 20',
                      'Wrote first name',
                      'Tied shoes',
                      'Used scissors properly',
                      'Recognized all letters',
                      'Drew a person',
                      'Completed puzzle independently',
                    ]}
                    value={reportForm.milestones || []}
                    onChange={(value) => setReportForm(prev => ({
                      ...prev,
                      milestones: value,
                    }))}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="communication" pt="md">
                <Stack gap="md">
                  <Textarea
                    label="Teacher's Notes"
                    placeholder="General notes about the day..."
                    value={reportForm.teacher_notes}
                    onChange={(event) => setReportForm(prev => ({
                      ...prev,
                      teacher_notes: event.currentTarget.value,
                    }))}
                    rows={4}
                    required
                  />

                  <Card withBorder>
                    <Title order={5} mb="sm">Parent Communication</Title>
                    <Stack gap="sm">
                      <Textarea
                        label="Message to Parents"
                        placeholder="Any specific message for parents..."
                        value={reportForm.parent_communication?.message || ''}
                        onChange={(event) => setReportForm(prev => ({
                          ...prev,
                          parent_communication: {
                            ...prev.parent_communication!,
                            message: event.currentTarget.value,
                          },
                        }))}
                        rows={3}
                      />
                      <Checkbox
                        label="Requires parent response"
                        checked={reportForm.parent_communication?.requires_response || false}
                        onChange={(event) => setReportForm(prev => ({
                          ...prev,
                          parent_communication: {
                            ...prev.parent_communication!,
                            requires_response: event.currentTarget.checked,
                          },
                        }))}
                      />
                    </Stack>
                  </Card>

                  <Card withBorder>
                    <Title order={5} mb="sm">Photos</Title>
                    <Group>
                      <Button
                        variant="light"
                        leftSection={<IconCamera size={16} />}
                        disabled
                      >
                        Add Photos (Coming Soon)
                      </Button>
                      <Text size="sm" c="dimmed">
                        {reportForm.photos?.length || 0} photos attached
                      </Text>
                    </Group>
                  </Card>
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>

      {/* Preview Modal */}
      <Modal
        opened={previewModalOpened}
        onClose={closePreviewModal}
        title="Report Preview"
        size="lg"
      >
        {selectedStudent && selectedReport && (
          <Stack gap="md">
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <Group>
                  <Avatar size="xl" radius="xl">
                    {selectedStudent.first_name[0]}{selectedStudent.last_name[0]}
                  </Avatar>
                  <div>
                    <Title order={3}>{selectedStudent.first_name} {selectedStudent.last_name}</Title>
                    <Text c="dimmed">
                      {new Date(selectedReport.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                  </div>
                </Group>
                <Badge color={getStatusColor(selectedReport.status)} size="lg">
                  {selectedReport.status}
                </Badge>
              </Group>

              <Divider mb="md" />

              <SimpleGrid cols={2} spacing="md">
                <div>
                  <Text size="sm" c="dimmed">Arrival</Text>
                  <Text fw={500}>{selectedReport.arrival_time || 'Not recorded'}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Departure</Text>
                  <Text fw={500}>{selectedReport.departure_time || 'Not recorded'}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Mood</Text>
                  <Group gap={4}>
                    <ThemeIcon color={getMoodColor(selectedReport.mood)} variant="light" size="sm">
                      {getMoodIcon(selectedReport.mood)}
                    </ThemeIcon>
                    <Text fw={500} tt="capitalize">{selectedReport.mood}</Text>
                  </Group>
                </div>
                <div>
                  <Text size="sm" c="dimmed">Behavior</Text>
                  <Text fw={500} tt="capitalize">{selectedReport.behavior.overall}</Text>
                </div>
              </SimpleGrid>
            </Card>

            {/* Meals Summary */}
            <Card withBorder>
              <Title order={5} mb="sm">Meals</Title>
              <Table>
                <Table.Tbody>
                  {Object.entries(selectedReport.meals).map(([meal, info]) => (
                    <Table.Tr key={meal}>
                      <Table.Td tt="capitalize">{meal}</Table.Td>
                      <Table.Td>
                        {info.eaten ? (
                          <Badge color="green" size="sm">Ate {info.amount}</Badge>
                        ) : (
                          <Badge color="gray" size="sm">Did not eat</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>{info.notes}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            {/* Activities */}
            {selectedReport.activities.length > 0 && (
              <Card withBorder>
                <Title order={5} mb="sm">Activities</Title>
                <Timeline active={selectedReport.activities.length} bulletSize={24} lineWidth={2}>
                  {selectedReport.activities.map((activity, index) => (
                    <Timeline.Item
                      key={index}
                      bullet={<IconPlayerPlay size={12} />}
                      title={activity.type}
                    >
                      <Text c="dimmed" size="sm">
                        {activity.duration} minutes • Participation: {activity.participation}
                      </Text>
                      {activity.notes && (
                        <Text size="sm" mt={4}>{activity.notes}</Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}

            {/* Teacher Notes */}
            <Card withBorder>
              <Title order={5} mb="sm">Teacher's Notes</Title>
              <Text>{selectedReport.teacher_notes}</Text>
            </Card>

            {/* Action Buttons */}
            <Group justify="center">
              <Button
                variant="light"
                leftSection={<IconPrinter size={16} />}
                disabled
              >
                Print Report
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                disabled
              >
                Download PDF
              </Button>
              <Button
                variant="light"
                leftSection={<IconCopy size={16} />}
                disabled
              >
                Copy Link
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default DailyReportsPage;